/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';

import {
  isCpd,
  languages,
  isWxoEnabled,
  isSlotFillingEnabled,
  isAgentBuilder
} from '@ibm-watson/shared/modules/ia/util/config';
import type { FeatureFlags } from '../../../../ts/feature-flags';
import { FeatureFlag } from '../../../../ts/feature-flags';
import { PlanName } from '../../../../ts/plan';
import { Language } from '../../../../ts/skill';
import type { RootState } from '../../../../ts/state';
import { MULTILINGUAL_CONVERSATIONAL_SEARCH_LANGUAGES } from '../../util/multiLingualConvoSearch';
import { selectAssistantLanguage } from '../assistants/core-selectors';
import { selectPlan, selectIsNewIa } from '../instance/selectors';
import { selectSkillLanguage } from '../skills/core-selectors';

export const areFeaturesUpdating = (state: RootState): boolean =>
  state.features.updating;

/**
 * Returns an object containing all the feature flags for the current instance.
 *
 * @deprecated Use {@link isFeatureEnabled} instead, specifying the flag being queried.
 * @param {Object} state The redux state
 * @return {Object} An immutable object that contains all available feature flags as keys, where the values are a
 * boolean stating whether the flag is enabled or not
 */
export const getInstanceFeatures = (state: RootState): FeatureFlags => {
  return state?.features?.rules;
};

/**
 * Returns true if the feature is enabled for the current instance.
 *
 * @param {Object} state The redux state
 * @param {string} featureName The feature name
 * @returns {boolean} True if the feature is available/enabled
 */
export const isFeatureEnabled = (
  state: RootState,
  featureName: FeatureFlag
): boolean => {
  const featureFlags = state.features?.rules ?? {};

  return Boolean(featureFlags[featureName]);
};

/**
 * Returns true if the feature is enabled for the current instance and the given language.
 *
 * @param {Object} state The redux state
 * @param {string} language The given language
 * @param {string} featureName The feature name
 * @returns {boolean} True if the feature is available/enabled
 */
export const isFeatureEnabledForLanguage = (
  state: RootState,
  language: Language,
  featureName: FeatureFlag
): boolean => {
  if (!language || !featureName) {
    return false;
  }

  const langFeatures = _.find(languages, { value: language }) || {};
  return (
    isFeatureEnabled(state, featureName) && Boolean(langFeatures[featureName])
  );
};

export const isActionsSynonymRecommendationsEnabled = (
  state: RootState
): boolean =>
  isFeatureEnabled(state, FeatureFlag.ACTIONS_SYNONYM_RECOMMENDATIONS);

export const isActionsClientActionsEnabled = (state: RootState): boolean =>
  isFeatureEnabled(state, FeatureFlag.ACTIONS_CLIENT_ACTIONS);

export const isSpellcheckEnabled = (state: RootState): boolean => {
  const language = selectSkillLanguage(state);
  return isFeatureEnabledForLanguage(state, language, FeatureFlag.SPELL_CHECK);
};

export const isPlusOrPremiumFeaturesEnabled = (state: RootState): boolean => {
  const isEnabled = isFeatureEnabled(
    state,
    FeatureFlag.ENABLE_PREMIUM_FEATURES
  );
  // all premium features are enabled on CPD, exceptional features are disabled using their own feature
  if (isEnabled || isCpd) {
    return true;
  }
  const planName = _.get(state, 'instance.plan.name');
  return planName !== PlanName.LITE && planName !== PlanName.STANDARD;
};

export const isPremiumFeaturesEnabled = (state: RootState): boolean => {
  const isEnabled = isFeatureEnabled(
    state,
    FeatureFlag.ENABLE_PREMIUM_FEATURES
  );
  // all premium features are enabled on CPD, exceptional features are disabled using their own feature
  if (isEnabled || isCpd) {
    return true;
  }
  const planName = _.get(state, 'instance.plan.name');
  return (
    planName === PlanName.PREMIUM ||
    planName === PlanName.ENTERPRISE ||
    planName === PlanName.ENTERPRISE_DATA_ISOLATION
  );
};

const MULTILINGUAL_INFO_GATHERING_LANGUAGES_SAAS =
  MULTILINGUAL_CONVERSATIONAL_SEARCH_LANGUAGES;

const MULTILINGUAL_INFO_GATHERING_LANGUAGES_CPD = Object.freeze([
  Language.English
]);

// Info gathering, also known as slot filling, is a feature within the actions settings panel under the generative ai
// tab. When enabled the feature gives the assistant the ability to intelligently fill in the answer to future steps, if
// the user provides that info ahead of time, instead of re-prompting the user once that step is reached. This feature
// was initially released as an English only feature. When the 'actions-generative-ai-slot-filling-multilingual' flag is
// enabled the generative ai tab and the slot filling toggle will be visible in an additional subset of assistant
// languages.
export const isInfoGatheringMultilingualEnabled = (
  state: RootState
): boolean => {
  const infoGatheringMultilingualEnabled = isFeatureEnabled(
    state,
    FeatureFlag.ACTIONS_GENERATIVE_AI_SLOT_FILLING_MULTILINGUAL
  );
  const language = selectSkillLanguage(state);

  // Generally in CPD we only support English, but if the slot filling feature
  // has been enabled in the deployment, this indicates we need to allow
  // all languages supported in SaaS.
  const multilingualInfoGatheringLanguages =
    isCpd && !isSlotFillingEnabled
      ? MULTILINGUAL_INFO_GATHERING_LANGUAGES_CPD
      : MULTILINGUAL_INFO_GATHERING_LANGUAGES_SAAS;

  // If the multilingual slot filling flag is enabled and the skills languages is one of the supported languages than
  // return true.
  return (
    infoGatheringMultilingualEnabled &&
    multilingualInfoGatheringLanguages.includes(language)
  );
};

/**
 * Controls the logic that relies on Conversational Search being enabled,
 * e.g. when displaying the Conversational Search toggle on Search settings
 * (discovery, ES, custom search).
 */
export const selectIsConversationalSearchEnabled = (
  state: RootState
): boolean => {
  const language = selectAssistantLanguage(state);

  const isConversationalSearchEnabled = isFeatureEnabled(
    state,
    FeatureFlag.DISCOVERY_LLM_CONVERSATIONAL_SEARCH
  );

  // Multilingual convo search is enabled on cp4d for SAP only & DISCOVERY_LLM_CONVERSATIONAL_SEARCH_MULTILINGUAL
  // flag is controlled in operators for that (not using AppConfig)
  // Multilingual is also enabled on wxo
  const isMultilingualConversationalSearchEnabled =
    isFeatureEnabled(
      state,
      FeatureFlag.DISCOVERY_LLM_CONVERSATIONAL_SEARCH_MULTILINGUAL
    ) || isWxoEnabled;

  return (
    isConversationalSearchEnabled &&
    (language === Language.English ||
      (isMultilingualConversationalSearchEnabled &&
        MULTILINGUAL_CONVERSATIONAL_SEARCH_LANGUAGES.includes(language)))
  );
};

// The generative ai tab in actions settings currently only holds one feature, slot filling. The generative ai will be
// visible if the generative ai flag is enabled, the user is using the new ia, and either, the skill / assistant
// language is in english or, the multilingual slot filling flag is enabled, and the user is working on an assistant
// using one of the supported languages.
export const isGenerativeAIEnabled = (state: RootState): boolean => {
  // If the generative ai flag is enabled and we're showing new IA then we need to verify the assistants language.
  if (
    isFeatureEnabled(state, FeatureFlag.ACTIONS_GENERATIVE_AI) &&
    selectIsNewIa(state)
  ) {
    // If multilingual info gathering is enabled and the skill language is one of the supported languages than the
    // generative AI tab should be visible.
    if (isInfoGatheringMultilingualEnabled(state)) {
      return true;
    }

    // Otherwise, the tab should only be visible if the skills language is english.
    const language = selectSkillLanguage(state);
    return language === Language.English;
  }

  return false;
};

// Base LLM is a feature for English Assistants in New IA when the generative AI
// and Base LLM AppConfig feature flags are enabled
export const selectIsBaseLLMEnabled = (state: RootState): boolean => {
  if (
    isFeatureEnabled(state, FeatureFlag.ACTIONS_GENERATIVE_AI) &&
    isFeatureEnabled(state, FeatureFlag.ACTIONS_GENERATIVE_AI_BASE_LLM) &&
    selectIsNewIa(state) &&
    MULTILINGUAL_CONVERSATIONAL_SEARCH_LANGUAGES.includes(
      selectSkillLanguage(state)
    )
  ) {
    return true;
  }

  return false;
};

// If we are in CPD 5.1.x, enable Base LLM only if it is an English assistant
// otherwise, follow the existing behaviour
export const selectShouldEnableBaseLLM = (state: RootState): boolean => {
  let shouldEnableBaseLLM: boolean;

  const isCpdVersion4 = selectIsCpdVersion4(state);
  const assistantLanguage = selectAssistantLanguage(state);
  const isEnglishAssistant = assistantLanguage === Language.English;
  const isBaseLLMEnabled = selectIsBaseLLMEnabled(state);

  if (isCpd && !isCpdVersion4) {
    shouldEnableBaseLLM = isBaseLLMEnabled && isEnglishAssistant;
  } else {
    shouldEnableBaseLLM = isBaseLLMEnabled;
  }

  return shouldEnableBaseLLM;
};

export const selectShouldCreatedSkillHaveBaseLLMEnabled = (
  state: RootState,
  language: Language
): boolean => {
  if (
    isFeatureEnabled(state, FeatureFlag.ACTIONS_GENERATIVE_AI) &&
    isFeatureEnabled(state, FeatureFlag.ACTIONS_GENERATIVE_AI_BASE_LLM) &&
    selectIsNewIa(state) &&
    language === Language.English &&
    isWxoEnabled
  ) {
    return true;
  }

  return false;
};

// HAP filtering is short for "Hate, abuse, and profanity" filtering, which is a feature within the actions settings
// panel under the generative ai tab. This is a moderation setting that can be configured in requests to WatsonX.AI,
// to allow more granular control over the sorts of responses that an LLM can respond with by filtering out any
// messages that may be deemed offensive to end users. For now, this is only available in English Assistants.
export const selectIsHapFilteringEnabled = (state: RootState): boolean => {
  if (
    isFeatureEnabled(state, FeatureFlag.ACTIONS_GENERATIVE_AI) &&
    isFeatureEnabled(state, FeatureFlag.ACTIONS_GENERATIVE_AI_HAP_FILTERING) &&
    selectIsNewIa(state)
  ) {
    return true;
  }

  return false;
};

export const selectShouldCreatedSkillHaveHAPFilteringEnabled = (
  state: RootState,
  language: Language
): boolean => {
  if (
    isFeatureEnabled(state, FeatureFlag.ACTIONS_GENERATIVE_AI) &&
    isFeatureEnabled(state, FeatureFlag.ACTIONS_GENERATIVE_AI_HAP_FILTERING) &&
    selectIsNewIa(state) &&
    language === Language.English
  ) {
    return true;
  }

  return false;
};

export const isInfoGatheringVerbatimEnabled = (state: RootState): boolean => {
  const generativeAIEnabled = isGenerativeAIEnabled(state);
  const verbatimEnabled = isFeatureEnabled(
    state,
    FeatureFlag.ACTIONS_GENERATIVE_AI_COLLECT_VERBATIM
  );
  return (
    verbatimEnabled &&
    (generativeAIEnabled || isInfoGatheringMultilingualEnabled(state))
  );
};

export const isContainmentAndCoverageEnabled = (state: RootState): boolean => {
  const isEnabled = isFeatureEnabled(
    state,
    FeatureFlag.ENABLE_PREMIUM_FEATURES
  );

  const planName = _.get(state, 'instance.plan.name');
  return planName !== PlanName.STANDARD || isEnabled;
};

// For Search to be enabled in old IA the discovery search feature flag must be enabled and the user must have plus or
// premium features enabled.
export const hasAccessToSearchSkillOldIA = (state: RootState): boolean =>
  isSearchFeatureEnabledOldIA(state) && isPlusOrPremiumFeaturesEnabled(state);

// Watson Discovery should be enabled only if the underlying flag is enabled
// (currently, everywhere except AWS, which does not support Watson Discovery),
// and we are either in WXA stand-alone, or, if we are under WXO, if we are
// in New IA.
// This means that Watson Discovery will NOT be available in AWS, in WXO
// CPD, or in WXO IBM Cloud if we are in Classic IA.
export const isSearchFeatureEnabledOldIA = (state: RootState): boolean => {
  return (
    isFeatureEnabled(state, FeatureFlag.DISCOVERY_SEARCH) &&
    (!isWxoEnabled || selectIsNewIa(state))
  );
};

/**
 * Determines the number of days logs are kept for a specific instance
 * @param state redux state
 */
export const maxDaysLogsAreKept = (state: RootState): number => {
  if (isFeatureEnabled(state, FeatureFlag.ENABLE_PREMIUM_FEATURES)) {
    return 90;
  } else {
    const planName = _.get(state, 'instance.plan.name');
    let days: number;
    switch (planName) {
      case PlanName.PREMIUM:
      case PlanName.ENTERPRISE_DATA_ISOLATION:
        days = 90;
        break;
      case PlanName.LITE:
        days = 7;
        break;
      default:
        days = 30;
    }
    return days;
  }
};

//check to see if segment is enabled on non enterprise plans
export const selectIsSegmentEnabledNonEnterprise = (
  state: RootState
): boolean =>
  isFeatureEnabled(state, FeatureFlag.CHANNELS_SEGMENT_NON_ENTERPRISE);

export const isAutolearnNewIAEnabled = (state: RootState): boolean => {
  // Autolearning is always disabled in WXO, and is otherwise
  // enabled only if the corresponding feature flag is enabled.
  return (
    !isWxoEnabled &&
    isFeatureEnabled(state, FeatureFlag.ACTIONS_AUTOLEARN_NEW_IA)
  );
};

export const isActionsDigressionReturnConfirmationEnabled = (
  state: RootState
): boolean => {
  const language = selectSkillLanguage(state);
  return language !== 'xx';
};

export const selectIsAccessUpdatesEnabled = (state: RootState): boolean =>
  isFeatureEnabled(state, FeatureFlag.IA_ACCESS_UPDATES);

// Only available in English, Spanish and Brazilian Portuguese languages
// NOT available in lite plans
export const isAnalyticsUnrecognizedEnabled = (state: RootState) => {
  const plan = selectPlan(state);
  if (plan === PlanName.LITE) {
    return false;
  }
  if (isFeatureEnabled(state, FeatureFlag.ANALYTICS_UNRECOGNIZED)) {
    const language = selectSkillLanguage(state);
    return (
      language === Language.English ||
      language === Language.Spanish ||
      language === Language.PortugueseBrazilian
    );
  }
  return false;
};

export const isActionsPrivacyEnabled = (state: RootState): boolean => {
  return selectIsNewIa(state);
};
export const isPhoneWxoEnabled = (state: RootState): boolean =>
  isFeatureEnabled(state, FeatureFlag.PHONE_AWS);

// This will be enabled in SaaS but not in CPD for the time being: eventually,
// it will be enabled in CPD 5.x but NOT CPD 4.x.
export const selectIsMilvusEnabled = (_state?: RootState): boolean => !isCpd;

/**
 * This flag will be enabled only when the server is running CPD at version 4.x,
 * where some features need to be disabled. It can be assumed to be false in
 * SaaS and newer versions of CPD.
 */
export const selectIsCpdVersion4 = (state: RootState): boolean =>
  isFeatureEnabled(state, FeatureFlag.CPD_VERSION_4);

// This will be enabled in SaaS and CPD 5.x, but NOT CPD 4.x.
export const selectIsInsightActionsEnabled = (state: RootState): boolean => {
  const plan = selectPlan(state);

  return (
    !selectIsCpdVersion4(state) &&
    (plan === PlanName.PLUS ||
      plan === PlanName.PREMIUM ||
      plan === PlanName.ENTERPRISE ||
      plan === PlanName.ENTERPRISE_DATA_ISOLATION ||
      (isCpd && plan === PlanName.STANDARD))
  );
};

/**
 * This feature is ONLY enabled in CPD 5.x and NOT 4.x.
 */
export const selectIsGenesysAudioConnectorInCpd = (
  state: RootState
): boolean => {
  return isFeatureEnabled(state, FeatureFlag.PHONE_GENESYS_AUDIO_CONNECTOR);
};

export const selectIsElevenlabsEnabled = (state: RootState): boolean => {
  return isFeatureEnabled(
    state,
    FeatureFlag.PHONE_GENESYS_AUDIO_CONNECTOR_ELEVENLABS
  );
};
/**
 * Check whether Agent Builder-related features (i.e. general-purpose answering,
 * skill-based actions, AI-guided actions, dialog coexistence and dialog in
 * Classic IA) should be hidden.
 *
 * These features are hidden when WXA is under WXO Agent Builder (unless a
 * flag has been specified to force their enablement).
 */
export const selectHideAgentBuilderFeatures = (state: RootState): boolean => {
  return (
    isAgentBuilder &&
    !isFeatureEnabled(state, FeatureFlag.IA_WXO_AGENT_BUILDER_FEATURES)
  );
};

/**
 * Check whether general-purpose answering (GPA) is enabled.
 * GPA is available only under WXO, and since it is currently in beta,
 * it must be disabled in CPD.
 * It must also be disabled if we are hiding features related to Agent
 * Builder.
 */
export const selectIsGeneralPurposeAnsweringEnabled = (
  state: RootState
): boolean => {
  return isWxoEnabled && !isCpd && !selectHideAgentBuilderFeatures(state);
};
